import { Event, EventPhase } from './Event';
import { BROADCAST_EVENTS, rt } from '../player/runtime';

/** @internal */
export interface ListenerEntry {
  fn: (e: any) => any;
  capture: boolean;
  priority: number;
}

/**
 * flash.events.EventDispatcher.
 *
 * - Listeners are plain functions (identity-compared), ordered by priority (desc), then registration.
 * - Registering the same (type, listener, useCapture) twice is a no-op (priority is not updated).
 * - Display-list dispatch: capture (root -> parent), target, bubble (parent -> root); the ancestor
 *   path is computed when dispatch starts; each node's listener list is snapshotted when reached.
 * - A listener throwing does not stop other listeners (Flash release-player behaviour); errors
 *   are routed to the runtime error policy.
 * - enterFrame/exitFrame/frameConstructed/activate/deactivate/render are broadcast events: adding
 *   such a listener registers the object in the runtime broadcast list (even when off-list).
 */
export class EventDispatcher {
  /** @internal type -> listeners (sorted). */
  $listeners: Map<string, ListenerEntry[]> | null;
  /** @internal IEventDispatcher target passed to the constructor. */
  $dispatchTarget: any;
  /** @internal broadcast registrations (see Runtime.registerBroadcast). */
  $bcast?: any;

  constructor(target?: any) {
    this.$listeners = null;
    this.$dispatchTarget = target ?? null;
  }

  addEventListener(type: string, listener: (e: any) => any, useCapture: boolean = false, priority: number = 0, _useWeakReference: boolean = false): void {
    if (typeof listener !== 'function') throw new TypeError('Error #2007: Parameter listener must be non-null.');
    type = String(type);
    useCapture = !!useCapture;
    priority = priority | 0;
    const map = this.$listeners || (this.$listeners = new Map());
    let list = map.get(type);
    if (!list) map.set(type, (list = []));
    for (const l of list) if (l.fn === listener && l.capture === useCapture) return;
    // Insert after all entries with priority >= new priority.
    let i = list.length;
    while (i > 0 && list[i - 1].priority < priority) i--;
    // Copy-on-write so that in-flight dispatch snapshots are unaffected.
    const next = list.slice();
    next.splice(i, 0, { fn: listener, capture: useCapture, priority });
    map.set(type, next);
    if (BROADCAST_EVENTS.has(type) && this.$acceptsBroadcast(type)) rt().registerBroadcast(this, type);
  }

  removeEventListener(type: string, listener: (e: any) => any, useCapture: boolean = false): void {
    const map = this.$listeners;
    if (!map) return;
    type = String(type);
    const list = map.get(type);
    if (!list) return;
    const idx = list.findIndex((l) => l.fn === listener && l.capture === !!useCapture);
    if (idx < 0) return;
    const next = list.slice();
    next.splice(idx, 1);
    if (next.length === 0) {
      map.delete(type);
      if (BROADCAST_EVENTS.has(type)) rt().releaseBroadcast(this, type);
    } else map.set(type, next);
  }

  hasEventListener(type: string): boolean {
    return !!this.$listeners && (this.$listeners.get(String(type))?.length ?? 0) > 0;
  }

  willTrigger(type: string): boolean {
    let o: any = this;
    while (o) {
      if (o.hasEventListener && o.hasEventListener(type)) return true;
      o = o.$parent ?? null;
    }
    return false;
  }

  dispatchEvent(event: Event): boolean {
    if (!(event instanceof Event)) throw new TypeError('Error #1034: Type Coercion failed: cannot convert to flash.events.Event.');
    if (event.$target) event = event.clone();
    return this.$dispatch(event);
  }

  /** @internal Which broadcast event types this object receives (DisplayObject overrides). */
  $acceptsBroadcast(type: string): boolean {
    return type === 'activate' || type === 'deactivate';
  }

  /** @internal Full capture/target/bubble dispatch without the clone check. */
  $dispatch(event: Event): boolean {
    const target = this.$dispatchTarget ?? this;
    event.$target = target;
    event.$stop = false;
    event.$stopImmediate = false;
    // Ancestor path (display objects only).
    let ancestors: any[] | null = null;
    let p = (target as any).$parent ?? null;
    while (p) {
      (ancestors || (ancestors = [])).push(p);
      p = p.$parent ?? null;
    }
    if (ancestors) {
      event.$phase = EventPhase.CAPTURING_PHASE;
      for (let i = ancestors.length - 1; i >= 0; i--) {
        if (event.$stop) break;
        invokeListeners(ancestors[i], event, true);
      }
    }
    event.$phase = EventPhase.AT_TARGET;
    if (!event.$stop) invokeListeners(this, event, false);
    if (event.$bubbles && ancestors) {
      event.$phase = EventPhase.BUBBLING_PHASE;
      for (let i = 0; i < ancestors.length; i++) {
        if (event.$stop) break;
        invokeListeners(ancestors[i], event, false);
      }
    }
    event.$currentTarget = null;
    return !event.$prevented;
  }

  /** @internal Broadcast delivery: target phase only, ignores propagation flags of other targets. */
  $dispatchBroadcast(event: Event): void {
    event.$target = this.$dispatchTarget ?? this;
    event.$phase = EventPhase.AT_TARGET;
    invokeListeners(this, event, false);
  }

  toString(): string {
    return `[object ${(this.constructor as any).name || 'EventDispatcher'}]`;
  }
}

/** @internal */
export function invokeListeners(node: any, event: Event, capture: boolean): void {
  const map: Map<string, ListenerEntry[]> | null = node.$listeners;
  if (!map) return;
  const list = map.get(event.$type);
  if (!list || list.length === 0) return;
  // `list` is copy-on-write: iterating it is a snapshot. Per the Flash docs, listeners removed
  // while the event is processed on this node still fire; listeners added do not.
  let any = false;
  for (let i = 0; i < list.length; i++) {
    const l = list[i];
    if (l.capture !== capture) continue;
    if (!any) {
      any = true;
      event.$currentTarget = node.$dispatchTarget ?? node;
    }
    if (event.$stopImmediate) break;
    try {
      l.fn.call(null, event);
    } catch (err) {
      rt().reportError(err, `event listener "${event.$type}"`);
    }
  }
}
